import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_BG, CONTACT } from "@/data/site";
import { MapPin, Star } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_BG}
          alt="Barbershop interior"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      </motion.div>

      {/* Diagonal barber stripe accent */}
      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-0 top-0 h-full w-2 barber-stripes opacity-70 hidden md:block origin-top"
      />

      <motion.div style={{ y: titleY, opacity: titleOpacity }} className="container-x relative z-10 pt-32 pb-20 w-full">
        <div className="max-w-5xl">
          <Reveal delay={0.1}>
            <div className="flex items-center gap-3 mb-8" data-testid="hero-location">
              <MapPin size={16} className="text-accent" />
              <span className="text-xs uppercase tracking-[0.35em] text-white/80">{CONTACT.city}</span>
              <span className="text-white/30">·</span>
              <span className="text-xs uppercase tracking-[0.35em] text-white/80 flex items-center gap-1">
                <Star size={12} className="fill-accent text-accent" /> 5.0 Rated
              </span>
            </div>
          </Reveal>

          <h1
            className="font-display text-7xl sm:text-[9rem] lg:text-[12rem] leading-[0.82] tracking-tight text-white"
            data-testid="hero-title"
          >
            <MaskedLines lines={["BLENDZ"]} lineClassName="block" stagger={0} />
            <MaskedLines
              lines={[<span key="accent" className="text-accent">.JAY</span>]}
              lineClassName="block"
              stagger={0.1}
            />
          </h1>

          <Reveal delay={0.6}>
            <p className="mt-10 max-w-xl text-lg text-white/70 leading-relaxed" data-testid="hero-subtitle">
              Lancaster's sharpest fades, cleanest line-ups and most precise shape-ups.
              One chair. No shortcuts. Just clean cuts at a clean price.
            </p>
          </Reveal>

          <Reveal delay={0.8}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a href="#book" className="btn-accent" data-testid="hero-book-cta">
                Book Appointment →
              </a>
              <a href="#portfolio" className="btn-outline" data-testid="hero-work-cta">
                See The Work
              </a>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="mt-20 grid grid-cols-3 gap-6 max-w-md">
              <Stat value="500+" label="Cuts" />
              <Stat value="5.0★" label="Rating" />
              <Stat value="$15" label="Shape-ups" />
            </div>
          </Reveal>
        </div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.4em] text-white/40 hidden md:flex flex-col items-center gap-3"
      >
        Scroll
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-white/30"
        />
      </motion.div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display text-4xl text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-dim mt-2">{label}</div>
  </div>
);
